import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useLang } from "../context/LanguageContext";
import { ArrowDownRight } from "lucide-react";

const easeElegant = [0.76, 0, 0.24, 1];

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.18]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);
  const titleY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  const titleLines = t("hero.title");
  const lines = Array.isArray(titleLines) ? titleLines : [String(titleLines)];

  return (
    <section
      id="top"
      ref={ref}
      className="relative w-full min-h-screen overflow-hidden bg-[#0A0A0A]"
      data-testid="hero-section"
    >
      {/* Parallax image */}
      <motion.div
        className="absolute inset-0 will-change-transform"
        style={{ y: imgY, scale: imgScale }}
      >
        <img
          src="https://images.unsplash.com/photo-1771003601843-8959745987e5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBpdGFsaWFuJTIwdmlsbGElMjB1bWJyaWElMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzg0MDQxMzUzfDA&ixlib=rb-4.1.0&q=85"
          alt="Umbrian villa with vineyards"
          className="w-full h-full object-cover"
          loading="eager"
        />
      </motion.div>
      <motion.div
        className="absolute inset-0 bg-[#0A0A0A]"
        style={{ opacity: overlayOpacity }}
        aria-hidden
      />
      <div className="absolute inset-0 radial-vignette pointer-events-none" aria-hidden />

      {/* Content */}
      <motion.div
        style={{ y: titleY }}
        className="relative z-10 mx-auto max-w-[1600px] min-h-screen px-6 md:px-10 lg:px-16 pt-32 md:pt-40 pb-24 flex flex-col justify-end"
      >
        {/* Overline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4, ease: easeElegant }}
          className="mb-10 flex items-center gap-4"
          data-testid="hero-overline"
        >
          <span className="block h-px w-16 bg-[#C5A059]" />
          <span className="overline text-[#C5A059]">{t("hero.overline")}</span>
        </motion.div>

        {/* Masked line-by-line title */}
        <h1
          className="font-serif text-[#F4F4F0] font-light text-[13vw] md:text-[9.6vw] lg:text-[8.4vw] leading-[0.95] tracking-tight max-w-[16ch]"
          data-testid="hero-heading"
        >
          {lines.map((line, i) => (
            <span key={i} className="mask-line">
              <motion.span
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{
                  duration: 1.4,
                  delay: 0.75 + i * 0.18,
                  ease: easeElegant,
                }}
                className={i === lines.length - 1 ? "italic text-[#C5A059]" : ""}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        {/* Bottom row */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 1.7, ease: easeElegant }}
          className="mt-14 md:mt-20 grid md:grid-cols-12 gap-8 items-end"
        >
          <p
            className="md:col-span-6 lg:col-span-5 text-[#F4F4F0]/80 text-base md:text-lg leading-[1.55] font-light"
            data-testid="hero-lede"
          >
            {t("hero.lede")}
          </p>

          <div className="md:col-span-6 lg:col-span-7 flex flex-col md:flex-row md:items-center md:justify-end gap-6 md:gap-10">
            <a href="#about" className="btn-gold" data-testid="hero-cta-primary">
              {t("hero.ctaPrimary")}
              <ArrowDownRight size={16} strokeWidth={1.4} />
            </a>
            <div className="flex items-center gap-3 text-[#A1A1AA]">
              <span className="block h-px w-8 bg-[#A1A1AA]/40" />
              <span className="overline !text-[#A1A1AA]">{t("hero.ctaSecondary")}</span>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 1 }}
        className="absolute bottom-6 right-6 md:right-10 z-10 flex items-center gap-3"
      >
        <span className="overline !text-[#F4F4F0]/50">Scroll</span>
        <span className="block h-8 w-px bg-[#C5A059]" />
      </motion.div>
    </section>
  );
};
